/**
 * TraversalToggle — DRR-135 user-switchable traversal lens
 *
 * Persists the user's traversal mode (PersonCentered | TaskCentric) to Valkey
 * via the switchTraversalMode mutation. Optimistic UI update, reverts on failure.
 */

import { useEffect } from 'react';
import { SegmentedControl, Skeleton, Group } from '@mantine/core';
import { IconHeartHandshake, IconChecklist } from '@tabler/icons-react';
import { useFhirLabels } from '@/hooks/useFhirLabels';
import {
  useMyTraversalModeQuery,
  useSwitchTraversalModeMutation,
  GqlTraversalMode,
} from '@/generated/graphql';

// ============================================================================
// TRAVERSAL TOGGLE (controlled)
// ============================================================================

interface TraversalToggleProps {
  readonly value: string;
  readonly onChange: (mode: string) => void;
}

export function TraversalToggle({ value, onChange }: TraversalToggleProps) {
  const { getFhirLabel } = useFhirLabels();
  const subjectLabel = getFhirLabel('Patient'); // "Resident" (ALF) or "Patient"

  const [switchMode] = useSwitchTraversalModeMutation();

  const handleChange = (newMode: string) => {
    onChange(newMode); // optimistic update
    switchMode({ variables: { mode: newMode as GqlTraversalMode } }).catch(() => {
      onChange(value); // revert on failure
    });
  };

  return (
    <SegmentedControl
      value={value}
      onChange={handleChange}
      size="xs"
      data={[
        {
          value: GqlTraversalMode.PERSON_CENTERED,
          label: (
            <Group gap={4}>
              <IconHeartHandshake size={12} />
              <span>My {subjectLabel}s</span>
            </Group>
          ),
        },
        {
          value: GqlTraversalMode.TASK_CENTRIC,
          label: (
            <Group gap={4}>
              <IconChecklist size={12} />
              <span>My Tasks</span>
            </Group>
          ),
        },
      ]}
    />
  );
}

// ============================================================================
// TRAVERSAL TOGGLE LOADER (fetches initial mode from backend)
// ============================================================================

interface TraversalToggleLoaderProps {
  readonly onModeResolved: (mode: string) => void;
  readonly value: string | null;
  readonly onChange: (mode: string) => void;
}

export function TraversalToggleLoader({
  onModeResolved,
  value,
  onChange,
}: TraversalToggleLoaderProps) {
  const { data, loading } = useMyTraversalModeQuery({ fetchPolicy: 'cache-and-network' });

  useEffect(() => {
    if (data?.myTraversalMode?.mode) {
      onModeResolved(data.myTraversalMode.mode);
    }
  }, [data, onModeResolved]);

  if (loading && value === null) {
    return <Skeleton height={28} width={180} radius="sm" />;
  }

  return (
    <TraversalToggle
      value={value ?? GqlTraversalMode.PERSON_CENTERED}
      onChange={onChange}
    />
  );
}
